import { useEffect, useState } from 'react';
import type { CSSProperties, FormEvent, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useMyAllTags, createTag, updateTag, deleteTag } from '../tareas/api';
import type { TagWithCount } from '../tareas/api';
import { useDialog } from '../ui/Dialogs';
import TeamSettings from './TeamSettings';
import TeamMembers from './TeamMembers';
import TeamInvites from './TeamInvites';
import ProfileSettings from './ProfileSettings';
import NotificationSettings from './NotificationSettings';
import MemberGroups from './MemberGroups';
import TeamStages from './TeamStages';
import PlanCard from '../billing/PlanCard';

const DEFAULT_COLOR = '#1d4ed8';

type Section = 'perfil' | 'notificaciones' | 'equipo' | 'plan' | 'miembros' | 'grupos' | 'invitaciones' | 'etapas' | 'tags';

const IC: Record<Section, ReactNode> = {
  perfil: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
    </svg>
  ),
  notificaciones: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  ),
  equipo: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="13" rx="2" /><path d="M8 8V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3" />
    </svg>
  ),
  plan: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" />
    </svg>
  ),
  miembros: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3.2" /><path d="M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1" /><path d="M16 4.2a3.2 3.2 0 0 1 0 6.1" /><path d="M21 20v-1a5 5 0 0 0-3.5-4.8" />
    </svg>
  ),
  grupos: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="9" r="2.6" /><circle cx="16" cy="9" r="2.6" /><path d="M3.5 19v-1a4 4 0 0 1 4-4h1a4 4 0 0 1 4 4v1" /><path d="M14 14h1.5a4 4 0 0 1 4 4v1" />
    </svg>
  ),
  invitaciones: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6.5l9 5.5 9-5.5" /><rect x="3" y="5" width="18" height="14" rx="2" />
    </svg>
  ),
  etapas: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="5" height="16" rx="1.5" /><rect x="10" y="4" width="5" height="10" rx="1.5" /><rect x="17" y="4" width="4" height="13" rx="1.5" />
    </svg>
  ),
  tags: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.6 3H5a2 2 0 0 0-2 2v7.6a2 2 0 0 0 .6 1.4l7.4 7.4a2 2 0 0 0 2.8 0l5.6-5.6a2 2 0 0 0 0-2.8L12 3.6A2 2 0 0 0 12.6 3z" /><circle cx="7.5" cy="7.5" r="1.2" />
    </svg>
  ),
};

// La configuración se divide en dos bloques, igual que el espacio de trabajo
// se separa de la cuenta personal: ajustes de TU cuenta (te siguen a todos los
// espacios) y ajustes DEL espacio activo (miembros, tags, facturación…).
const ACCOUNT_SECTIONS: Array<{ key: Section; label: string }> = [
  { key: 'perfil', label: 'Perfil' },
  { key: 'notificaciones', label: 'Notificaciones' },
];
const WORKSPACE_SECTIONS: Array<{ key: Section; label: string }> = [
  { key: 'equipo', label: 'General' },
  { key: 'miembros', label: 'Miembros' },
  { key: 'grupos', label: 'Grupos' },
  { key: 'invitaciones', label: 'Invitaciones' },
  { key: 'etapas', label: 'Etapas' },
  { key: 'tags', label: 'Tags' },
  { key: 'plan', label: 'Facturación' },
];
const SECTIONS = [...ACCOUNT_SECTIONS, ...WORKSPACE_SECTIONS];

export default function PerfilPage() {
  const { profile, teams, activeTeamId } = useAuth();
  const activeTeam = teams.find((t) => t.id === activeTeamId) ?? null;
  // Sección inicial por URL: ?checkout=... (vuelta de Stripe) abre Plan;
  // ?seccion=<clave> permite enlazar directo a una pestaña (p. ej. "Invitar a
  // miembros" del sidebar abre Invitaciones).
  const [section, setSection] = useState<Section>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout')) return 'plan';
    const s = params.get('seccion');
    const valid = SECTIONS.map((x) => x.key) as string[];
    return s && valid.includes(s) ? (s as Section) : 'perfil';
  });

  // Si llega un ?seccion=<clave> nuevo estando ya en la página (p. ej. se pulsa
  // "Invitar a miembros" desde el sidebar), saltamos a esa pestaña. No pisa los
  // clics en el menú lateral, que no tocan la query.
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('checkout')) { setSection('plan'); return; }
    const s = params.get('seccion');
    const valid = SECTIONS.map((x) => x.key) as string[];
    if (s && valid.includes(s)) setSection(s as Section);
  }, [location.search]);

  return (
    <div style={styles.wrap}>
      <Link to="/bandeja" style={styles.back}>‹ Bandeja</Link>
      <h1 style={styles.title}>Configuración</h1>

      <div style={styles.layout}>
        <nav style={styles.nav}>
          <p style={styles.navGroupLabel}>Cuenta</p>
          {ACCOUNT_SECTIONS.map((s) => (
            <NavButton key={s.key} item={s} active={s.key === section} onClick={() => setSection(s.key)} />
          ))}

          <p style={{ ...styles.navGroupLabel, marginTop: 18 }} title={activeTeam?.nombre}>
            {activeTeam?.nombre ?? 'Espacio de trabajo'}
          </p>
          {WORKSPACE_SECTIONS.map((s) => (
            <NavButton key={s.key} item={s} active={s.key === section} onClick={() => setSection(s.key)} />
          ))}
        </nav>

        <div style={styles.content}>
          {section === 'perfil' && <ProfileSettings />}
          {section === 'notificaciones' && <NotificationSettings />}
          {section === 'equipo' && <TeamSettings />}
          {section === 'plan' && <PlanCard />}
          {section === 'miembros' && <TeamMembers />}
          {section === 'grupos' && <MemberGroups userId={profile?.id} />}
          {section === 'invitaciones' && <TeamInvites />}
          {section === 'etapas' && <TeamStages />}
          {section === 'tags' && <TagsSection userId={profile?.id} />}
        </div>
      </div>
    </div>
  );
}

function NavButton({ item, active, onClick }: {
  item: { key: Section; label: string };
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ ...styles.navItem, ...(active ? styles.navItemActive : null) }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#f8fafc'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ ...styles.navIcon, color: active ? '#1d4ed8' : '#64748b' }}>{IC[item.key]}</span>
      {item.label}
    </button>
  );
}

function TagsSection({ userId }: { userId?: string }) {
  const { tags, refetch } = useMyAllTags(userId);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_COLOR);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newName.trim() || !userId) return;
    setCreating(true);
    setError('');
    try {
      await createTag({ nombre: newName.trim(), color: newColor }, userId);
      setNewName('');
      setNewColor(DEFAULT_COLOR);
      await refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <p style={styles.intro}>
        Las tags son privadas (solo tú las ves) y propias de este espacio de trabajo.
        Aplícalas a los hilos para organizar tu trabajo. Las tags sin hilos asociados no
        aparecen en el sidebar.
      </p>

      <section style={styles.card}>
        <h2 style={styles.cardTitle}>Crear nueva tag</h2>
        <form onSubmit={handleCreate} style={styles.createForm}>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nombre de la tag (ej. Urgente)"
            maxLength={40}
            required
            style={styles.input}
          />
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            style={styles.colorInput}
            title="Color"
          />
          <button type="submit" disabled={creating} style={styles.btnPrimary}>
            {creating ? 'Creando…' : 'Crear tag'}
          </button>
        </form>
        {error && <p style={styles.error}>{error}</p>}
      </section>

      <section style={styles.card}>
        <h2 style={styles.cardTitle}>Tus tags ({tags.length})</h2>
        {tags.length === 0 ? (
          <p style={styles.empty}>Aún no has creado ninguna tag.</p>
        ) : (
          <div style={styles.list}>
            {tags.map((t) => (
              <TagRow key={t.id} tag={t} onSaved={refetch} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

interface TagRowProps {
  tag: TagWithCount;
  onSaved: () => Promise<void>;
}

function TagRow({ tag, onSaved }: TagRowProps) {
  const { confirm, alert } = useDialog();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tag.nombre);
  const [color, setColor] = useState(tag.color);
  const [busy, setBusy] = useState(false);

  const handleSave = async () => {
    setBusy(true);
    try {
      await updateTag(tag.id, { nombre: name, color });
      setEditing(false);
      await onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      alert({ title: 'No se pudo guardar', message: msg });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Borrar tag',
      message: tag.tasksCount > 0
        ? `Esta tag está aplicada en ${tag.tasksCount} hilo${tag.tasksCount === 1 ? '' : 's'}. Si la borras, se desvincula de todos. ¿Continuar?`
        : '¿Seguro que quieres borrar esta tag?',
      confirmLabel: 'Borrar',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteTag(tag.id);
      await onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      alert({ title: 'No se pudo borrar', message: msg });
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div style={styles.row}>
        <input
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          style={styles.colorInput}
        />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={40}
          style={{ ...styles.input, flex: 1 }}
        />
        <button type="button" onClick={handleSave} disabled={busy} style={styles.btnPrimarySmall}>
          Guardar
        </button>
        <button
          type="button"
          onClick={() => { setName(tag.nombre); setColor(tag.color); setEditing(false); }}
          style={styles.btnGhost}
        >
          Cancelar
        </button>
      </div>
    );
  }

  return (
    <div style={styles.row}>
      <span style={{ ...styles.dot, background: tag.color }} />
      <span style={{ flex: 1, color: tag.color, fontWeight: 600 }}>{tag.nombre}</span>
      <span style={styles.count}>
        {tag.tasksCount > 0 ? `${tag.tasksCount} hilo${tag.tasksCount === 1 ? '' : 's'}` : 'sin hilos'}
      </span>
      <button type="button" onClick={() => setEditing(true)} style={styles.btnGhost}>
        Editar
      </button>
      <button type="button" onClick={handleDelete} disabled={busy} style={styles.btnDanger}>
        Borrar
      </button>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  wrap: { padding: '32px 40px', maxWidth: 960, margin: '0 auto' },
  back: {
    color: '#64748b',
    fontSize: 13,
    textDecoration: 'none',
    display: 'inline-block',
    marginBottom: 12,
  },
  title: {
    fontSize: 26,
    fontWeight: 800,
    color: '#0f172a',
    margin: '0 0 24px',
    letterSpacing: '-0.4px',
  },
  layout: { display: 'flex', alignItems: 'flex-start', gap: 28 },
  nav: {
    width: 196,
    minWidth: 196,
    position: 'sticky',
    top: 32,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    padding: '9px 12px',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    color: '#334155',
    cursor: 'pointer',
    transition: 'background .12s ease',
  },
  navGroupLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#94a3b8',
    margin: '0 0 4px',
    padding: '0 12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  navItemActive: { background: '#eff5ff', color: '#1d4ed8' },
  navIcon: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20 },
  content: { flex: 1, minWidth: 0 },
  intro: { color: '#64748b', fontSize: 14, margin: '0 0 16px', lineHeight: 1.55 },
  card: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: 22,
    marginBottom: 18,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#0f172a',
    margin: '0 0 16px',
  },
  createForm: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  input: {
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    padding: '9px 12px',
    fontSize: 14,
    outline: 'none',
    background: '#fff',
    flex: 1,
  },
  colorInput: {
    width: 40,
    height: 40,
    border: 'none',
    padding: 0,
    background: 'transparent',
    cursor: 'pointer',
  },
  btnPrimary: {
    background: '#0f172a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 18px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnPrimarySmall: {
    background: '#0f172a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnGhost: {
    background: 'transparent',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    padding: '6px 12px',
    cursor: 'pointer',
    color: '#475569',
    fontSize: 13,
    fontWeight: 600,
  },
  btnDanger: {
    background: 'transparent',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: '6px 12px',
    cursor: 'pointer',
    color: '#dc2626',
    fontSize: 13,
    fontWeight: 600,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 4px',
    borderBottom: '1px solid #f1f5f9',
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  count: {
    fontSize: 12,
    color: '#94a3b8',
  },
  empty: {
    color: '#94a3b8',
    fontSize: 14,
    margin: 0,
  },
  error: {
    color: '#dc2626',
    fontSize: 13,
    marginTop: 12,
    marginBottom: 0,
  },
};
